import { type AxiosInstance, isAxiosError } from "axios";
import { logError } from "../../datadog";
import type {
  CollectionClient,
  CollectionMaterialRecord,
  CollectionMembership,
  CollectionRecord,
  MaterialSourceDiscriminator,
  ProjectOption,
} from "./collection-client.types";
import {
  COLLECTIONS_REQUEST_TIMEOUT_MS,
  CX_APPLICATION_API_URLS,
  MEMBERSHIP_MAX_PAGES,
  MEMBERSHIP_PAGE_LIMIT,
  PROJECT_OPTIONS_MAX_PAGES,
  PROJECT_OPTIONS_PAGE_LIMIT,
} from "./collections.constants";

/** One row of `GET /v1/collections/membership`, as the service sends it. */
interface MembershipStateDto {
  id: string;
  name: string;
  contains: boolean;
  membershipId?: string;
}

interface MembershipStatePageDto {
  items: MembershipStateDto[];
  cursor?: string;
}

/** `CollectionMaterial` from `POST /v1/collections/{id}/materials`. */
interface CollectionMaterialDto {
  id: string;
  collectionId: string;
  sourceDiscriminator: MaterialSourceDiscriminator;
  sourceRefId: string;
  createdAt: string;
}

/** The subset of `Collection` from `POST /v1/collections` the popover uses. */
interface CollectionDto {
  id: string;
  name: string;
  createdAt: string;
}

interface ProjectOptionsPageDto {
  items: { projectId: string; name: string }[];
  cursor?: string;
}

interface MoodboardPresignDto {
  imageId: string;
  presignedPutUrl: string;
}

/**
 * The HTTP status a client call failed with, if it got that far.
 *
 * @param error - Whatever a {@link CollectionClient} call rejected with.
 * @returns The status, or `undefined` for a network error / timeout.
 */
export const getHttpStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

export interface HttpCollectionClientOptions {
  /** `cx-srv-application` root, e.g. {@link CX_APPLICATION_API_URLS}`.qa`. */
  baseUrl: string;
  /**
   * Returns the axios instance to call through. Resolved per request so the
   * app's shared, token-attaching instance is picked up as it is (re)created.
   */
  getAxios: () => AxiosInstance;
}

/**
 * Picks the `cx-srv-application` base URL for the host Color Finder runs on:
 * QA for `qa-*` hosts and `localhost` (the same rule as `app-config.tsx`),
 * production otherwise.
 *
 * @param hostname - `window.location.hostname`.
 * @returns The service root, without a trailing slash.
 */
export const getCxApplicationApiUrl = (hostname: string): string =>
  hostname.startsWith("qa-") || hostname === "localhost"
    ? CX_APPLICATION_API_URLS.qa
    : CX_APPLICATION_API_URLS.production;

const toMembership = (dto: MembershipStateDto): CollectionMembership =>
  dto.contains
    ? {
        collectionId: dto.id,
        name: dto.name,
        contains: true,
        membershipId: dto.membershipId,
      }
    : { collectionId: dto.id, name: dto.name, contains: false };

/**
 * {@link CollectionClient} over `cx-srv-application`'s Collections routes
 * (CX-3786; contract CX-3993/CX-3994). It is the anti-corruption layer: the
 * service's field names (`id` for both the collection and the membership) are
 * mapped onto the client types here, so the hook and UI never see them.
 *
 * The browser calls the CX shell directly, so the service must allow the Color
 * Finder origin in CORS. Errors (4xx/5xx/network/timeout) are rethrown as
 * they come; the service answers a missing or rejected token with 403.
 *
 * @param options - Base URL and the axios instance to call through.
 * @returns A client for the heart popover.
 * @example
 * const client = createHttpCollectionClient({
 *   baseUrl: getCxApplicationApiUrl(window.location.hostname),
 *   getAxios: () => getAuthenticatedAxios().axios,
 * });
 */
export const createHttpCollectionClient = ({
  baseUrl,
  getAxios,
}: HttpCollectionClientOptions): CollectionClient => {
  const config = { timeout: COLLECTIONS_REQUEST_TIMEOUT_MS };
  const collectionPath = (collectionId: string) =>
    `${baseUrl}/v1/collections/${encodeURIComponent(collectionId)}`;

  return {
    async getMembership(material) {
      const rows: CollectionMembership[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MEMBERSHIP_MAX_PAGES; page++) {
        const { data } = await getAxios().get<MembershipStatePageDto>(
          `${baseUrl}/v1/collections/membership`,
          {
            ...config,
            params: {
              sourceDiscriminator: material.sourceDiscriminator,
              sourceRefId: material.sourceRefId,
              limit: MEMBERSHIP_PAGE_LIMIT,
              ...(cursor ? { cursor } : {}),
            },
          },
        );
        rows.push(...data.items.map(toMembership));
        cursor = data.cursor;
        if (!cursor) {
          return rows;
        }
      }
      // Past the cap the list is incomplete: the popover shows what it has and
      // the gap is reported rather than silently dropped.
      logError(new Error("Collection membership truncated at page cap"), {
        source: "createHttpCollectionClient.getMembership",
        pages: MEMBERSHIP_MAX_PAGES,
        rows: rows.length,
      });
      return rows;
    },

    async addMaterial(collectionId, material) {
      // Only the reference: the service rejects server-owned fields with 400.
      // 201 = added, 200 = already there (idempotent); both carry the membership.
      const { data } = await getAxios().post<CollectionMaterialDto>(
        `${collectionPath(collectionId)}/materials`,
        {
          sourceDiscriminator: material.sourceDiscriminator,
          sourceRefId: material.sourceRefId,
        },
        config,
      );
      const record: CollectionMaterialRecord = {
        membershipId: data.id,
        collectionId: data.collectionId,
        sourceDiscriminator: data.sourceDiscriminator,
        sourceRefId: data.sourceRefId,
        createdAt: data.createdAt,
      };
      return record;
    },

    async removeMaterial(collectionId, membershipId) {
      await getAxios().delete(
        `${collectionPath(collectionId)}/materials/${encodeURIComponent(membershipId)}`,
        config,
      );
    },

    async createCollection(body) {
      const { data } = await getAxios().post<CollectionDto>(
        `${baseUrl}/v1/collections`,
        body,
        config,
      );
      const record: CollectionRecord = {
        id: data.id,
        name: data.name,
        createdAt: data.createdAt,
      };
      return record;
    },

    async listProjectOptions() {
      const options: ProjectOption[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < PROJECT_OPTIONS_MAX_PAGES; page++) {
        const { data } = await getAxios().get<ProjectOptionsPageDto>(
          `${baseUrl}/v1/projects/options`,
          {
            ...config,
            params: {
              limit: PROJECT_OPTIONS_PAGE_LIMIT,
              ...(cursor ? { cursor } : {}),
            },
          },
        );
        options.push(
          ...data.items.map(({ projectId, name }) => ({ id: projectId, name })),
        );
        cursor = data.cursor;
        if (!cursor) {
          break;
        }
      }
      return options;
    },

    async addMoodboardImage(collectionId, file) {
      const moodboardPath = `${collectionPath(collectionId)}/moodboard`;
      const { data } = await getAxios().post<MoodboardPresignDto>(
        moodboardPath,
        {
          fileName: file.name,
          contentType: file.type,
          contentLength: file.size,
        },
        config,
      );
      try {
        // Plain fetch: the signed URL carries its own authority, so it must not
        // get the app's Authorization header. `Content-Type` is part of the
        // signature and has to match the presign request.
        const response = await fetch(data.presignedPutUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) {
          throw new Error(`Image upload failed with ${response.status}`);
        }
      } catch (error) {
        // The service records the image at presign time; drop it best-effort so
        // a failed upload doesn't leave a broken image in the collection.
        await getAxios()
          .delete(
            `${moodboardPath}/${encodeURIComponent(data.imageId)}`,
            config,
          )
          .catch(() => undefined);
        throw error;
      }
    },
  };
};
